<template>
  <div class="slds-form-element" :class="{'slds-size--medium': !small}">
    <!-- Label -->
    <label class="slds-form-element__label" :for="`select-${id}`" v-if="label">
      <abbr class="slds-required" title="required" v-if="required || (validation && validation.includes('required'))">*</abbr>
        {{ label }}
      <Tooltip :text="tooltip" v-if="tooltip" :direction="tooltipDirection" />
    </label>

    <!-- No label -->
    <label class="slds-form-element__label" :for="`select-${id}`" v-if="noLabel">
      {{nbsp}}
    </label>
    <!-- Multipicklist selected values -->
    <template v-if="multipicklist">
      <div v-if="localValue && localValue.length" class="slds-listbox_selection-group selection-box">
        <ul class="slds-listbox slds-listbox_horizontal listbox" role="listbox" aria-label="Selected Options:" aria-orientation="horizontal">
          <li class="slds-listbox-item" :class="{'first-item': index === 0}" role="presentation" v-for="(option, index) in localValue" :key="index">
            <span class="slds-pill" role="option" :tabindex="index" aria-selected="true">
              <span class="slds-pill__label" :title="option">{{getLabelFromValue(option)}}</span>
              <span class="slds-icon_container slds-pill__remove" :title="$t('action.remove')" @click="askForConfirmation(option)" v-if="canRemoveElements && !disabled && !(hidePicklist && localValue.length === 1)">
                <svg class="slds-icon slds-icon_x-small slds-icon-text-default" aria-hidden="true">
                  <use xlink:href="../../assets/symbols.svg#close"></use>
                </svg>
              </span>
            </span>
          </li>
        </ul>
      </div>

      <div class="slds-listbox_selection-group selection-box" v-else-if="hidePicklist">
        {{nbsp}}
      </div>
    </template>

    <!-- Picklist -->
    <div class="slds-combobox_container" v-click-outside="{callback: closeEvent, enable: this.multipicklist}" v-if="!hidePicklist">
      <div class="slds-combobox slds-dropdown-trigger slds-dropdown-trigger_click" :class="{'slds-is-open': open}" aria-expanded="false" aria-haspopup="listbox" role="combobox">
        <div class="slds-combobox__form-element slds-input-has-icon slds-input-has-icon_right" role="none" :class="{'slds-has-error': !valid}">
          <input ref="input" :disabled="disabled" type="text" v-model="searchKey" class="slds-input slds-combobox__input" :id="`combobox-id-${id}`" :aria-controls="`listbox-id-${id}`" :readonly="disabled || !searchable" autoComplete="off" role="textbox" :placeholder="placeholder" @click="toggleOpen" v-click-outside="{callback: closeEvent, enable: !this.multipicklist}"/>
          <span class="slds-icon_container slds-icon-utility-down slds-input__icon slds-input__icon_right">
            <svg class="slds-icon slds-icon slds-icon_x-small slds-icon-text-default" aria-hidden="true">
              <use xlink:href="../../assets/symbols.svg#down"></use>
            </svg>
          </span>
        </div>
        <div class="slds-form-element__help error" v-if="!valid && !open">{{ error }}</div>

        <div :id="`listbox-id-${id}`" :class="`slds-dropdown_length-${visibleItems}`" class="slds-dropdown slds-dropdown_fluid" role="listbox" v-if="filteredList && filteredList.length">
          <ul class="slds-listbox slds-listbox_vertical" role="presentation">
            <li role="presentation" class="slds-listbox__item" v-for="(option, index) in filteredList" :key="index" @click="toggleItemSelection(option.value)">
              <div :id="`option-${index}`" :aura-selected="isItemSelected(option.value)" :aria-checked="isItemSelected(option.value)" class="slds-media slds-listbox__option slds-listbox__option_plain slds-media_small" role="option">
                <span class="slds-media__figure slds-listbox__option-icon" v-if="isItemSelected(option.value)">
                  <span class="slds-icon_container slds-icon-utility-check slds-current-color">
                    <svg class="slds-icon slds-icon_x-small selected" aria-hidden="true">
                      <use xlink:href="../../assets/symbols.svg#check"></use>
                    </svg>
                  </span>
                </span>
                <span class="slds-media__body">
                  <span class="slds-truncate" v-if="searchable" :title="option.label" v-html="$options.filters.highlight(option.label, searchKey)" />
                  <span class="slds-truncate" v-else :title="option.label">{{option.label}}</span>
                </span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Remove item confirmation modal -->
    <transition name="fade">
      <Modal
        v-if="showDeleteConfirmModal"
        :title="$t('campaignDesigner.targetDefinition.confirmFilterDisableTitle')"
        :neutral-button-text="$t('action.cancel')"
        :positive-button-text="$t('action.remove')"
        @close="closeModal"
        @neutralButtonClick="closeModal()"
        @positiveButtonClick="removeItem(selectedOption)">

        <div class="slds-text-heading_small slds-text-align_center">
          {{$t('campaignDesigner.targetDefinition.confirmFilterDisableText')}}
        </div>

      </Modal>
    </transition>
  </div>
</template>
<script>

import Modal from '../Modal'
import Tooltip from '../slds/Tooltip'
import Validator from '../../validation/Validator.vue'
import { arrayEquals } from '../utils/utils'
import _ from 'lodash'

const MAX_ITEMS_TO_SHOW = 200

export default {
  components: {
    Modal,
    Tooltip
  },
  mixins: [Validator],
  computed: {
    nbsp () {
      return '\xa0'
    },
    placeholder () {
      if (this.multipicklist) {
        return this.$tc('picklist.values', (this.localValue || []).length, { count: (this.localValue || []).length })
      } else {
        if (this.includeAllValue || this.localValue) {
          const label = this.getLabelFromValue(this.localValue)
          return label
        } else {
          return this.$tc('picklist.values', 0, { count: 0 })
        }
      }
    }
    // filteredList () {
    //   if (!this.searchKey) {
    //     return this.localOptions
    //   } else {
    //     return this.localOptions.filter(option => option.label.toLowerCase().includes(this.searchKey.toLowerCase()))
    //   }
    // }
  },
  props: {
    canRemoveElements: { type: Boolean, default: true },
    disabled: Boolean,
    hidePicklist: Boolean,
    includeAllValue: Boolean,
    label: String,
    labelsPrefix: String,
    multipicklist: Boolean,
    noLabel: Boolean,
    options: Array,
    required: Boolean,
    searchable: Boolean,
    showEmptyValue: Boolean,
    small: Boolean,
    sortLabel: Boolean,
    tooltip: String,
    tooltipDirection: String,
    value: [String, Array, Boolean, Object],
    visibleItems: {
      type: String,
      default: '5',
      required: false
    }
  },
  data () {
    return {
      filteredList: [],
      id: parseInt(Math.random() * 1000000),
      localDisabled: false,
      localOptions: [],
      localValue: this.multipicklist ? [] : '',
      open: false,
      searchKey: '',
      selectedOption: {},
      showDeleteConfirmModal: false
    }
  },
  methods: {
    filterList (searchKey) {
      this.filteredList = []
      const normalizedSearchKey = searchKey || ''
      let shortList
      let filteredList
      if (this.localOptions.length <= MAX_ITEMS_TO_SHOW) {
        shortList = this.localOptions
      } else {
        shortList = this.localOptions.slice(0, MAX_ITEMS_TO_SHOW)
      }

      if (!normalizedSearchKey) {
        filteredList = shortList
      } else {
        filteredList = this.localOptions
          .filter(option => option.label.toLowerCase().includes(normalizedSearchKey.toLowerCase()))
          .slice(0, MAX_ITEMS_TO_SHOW)
      }

      // Add local value in filtered list
      if (this.multipicklist) {
        if (this.localValue && this.localValue.length) {
          const delta = []
          this.localValue.forEach(val => {
            if (!filteredList.find(e => e.value === val)) {
              const object = {
                label: this.getLabelFromValue(val),
                value: val
              }
              delta.push(object)
            }
          })

          filteredList = [...delta, ...filteredList]
        }
      } else {
        if (this.localValue && !filteredList.find(opt => opt.value === this.localValue)) {
          const object = {
            label: this.getLabelFromValue(this.localValue),
            value: this.localValue
          }

          filteredList = [object, ...filteredList]
        }
      }
      this.filteredList = this.sortLabel ? filteredList.sort( (a, b) => a.label ? a.label.localeCompare(b.label) : -1) : filteredList
      let ind = this.filteredList.findIndex(obj => obj.label == this.$t('general.all'))
      //If we have 'All', don't sort that, put it as first element in the picklist
      if (ind !== -1) {
        let elem = this.filteredList.splice(ind,1)[0] //Take 'All' out
        this.filteredList.unshift(elem) //Put 'All' in first index
      }
    },
    setError (error) {
      this.error = error
    },
    askForConfirmation (option) {
      if (this.hidePicklist) {
        this.selectedOption = option
        this.showDeleteConfirmModal = true
      } else {
        this.removeItem(option)
      }
    },
    closeModal () {
      this.selectedOption = {}
      this.showDeleteConfirmModal = false
    },
    closeEvent () {
      if (this.open) {
        this.open = false
        if (this.multipicklist && this.searchKey) {
          this.searchKey = ''
        }
      }
    },
    focus () {
      this.open = true
      this.$refs.input.focus()
    },
    getLabelFromValue (value) {
      try {
        const obj = this.localOptions.find(option => {
          if (typeof option.value !== 'object') {
            return (('' + option.value) === ('' + value)) || (option.value && value.value && (option.value === value.value))
          } else {
            return (('' + option.value.value) === ('' + value.value)) || (option.value.value && value.value && (option.value.value === value.value))
          }
        })

        if (obj) return obj.label
        else return ''
      } catch (error) {
        console.error(`Error in Picklist ${this.label} for value ${JSON.stringify(value)}`, error)
      }
    },
    isItemSelected (value) {
      if (this.multipicklist) {
        return (this.localValue || []).some(option => option === value || (option.value && value.value && option.value === value.value))
      } else {
        return this.localValue === value || (this.localValue && value && this.localValue.value && value.value && this.localValue.value === value.value)
      }
    },
    mapLocalOptions (options) {
      // this.localOptions = this.labelsPrefix ? options.map(option => { return { label: this.$t(`${this.labelsPrefix}.${option}`), value: option } }) : options
      try {
        if (!options) {
          this.localOptions = []
        } else if (options.length && typeof options[0] === 'object') {
          this.localOptions = options
        } else {
          this.localOptions = options.map(option => {
            return {
              label: this.labelsPrefix ? this.$t(`${this.labelsPrefix}.${option}`) : option,
              value: option
            }
          })
        }
        if (this.includeAllValue) {
          if(this.localOptions[0].value === '')
          {
             this.localOptions.splice(0, 1, { label: this.$t('general.all'), value: '' })
          }
          else if (this.localOptions[0].label !== this.$t('general.all')) {
            this.localOptions.splice(0, 0, { label: this.$t('general.all'), value: '' })
          }
        }
        if (this.showEmptyValue) {
          if(this.localOptions[0].value === '')
          {
            this.localOptions.splice(0, 1, { label: this.$tc('picklist.values', 0, { count: 0 }), value: '' })
          }
          else if (this.localOptions[0].label !== this.$tc('picklist.values', 0, { count: 0 })) {
            this.localOptions.splice(0, 0, { label: this.$tc('picklist.values', 0, { count: 0 }), value: '' })
          }

        }
      } catch (error) {
        console.error(`Error in Picklist ${this.label} for options ${JSON.stringify(options)}`, error)
      }
    },
    removeItem (option) {
      const index = this.localValue.indexOf(option)
      if (index >= 0) {
        this.localValue.splice(index, 1)
      }
      this.selectedOption = {}
      this.filterList()
      this.showDeleteConfirmModal = false
    },
    toggleItemSelection (option) {
      if (this.multipicklist) {
        if (this.includeAllValue && option === '') {
          // User selected 'All' option
          if (this.localOptions.every(v => !v.value || this.isItemSelected(v.value))) {
            this.localValue = []
          } else {
            this.localValue = []
            this.localOptions.forEach(option => {
              if (option.value) {
                this.localValue.push(option.value)
              }
            })
          }
        } else {
          const index = this.localValue.indexOf(option)
          if (index >= 0) {
            this.localValue.splice(index, 1)
          } else {
            this.localValue.push(option)
          }
        }
      } else {
        this.localValue = option
      }

      this.filterList()

      this.$emit('input', this.localValue)
      this.searchKey = ''
      this.open = this.multipicklist && !(this.localValue.length === this.localOptions.length)
      this.clearValidationError()
    },
    toggleOpen () {
      if (!this.disabled) {
        this.open = !this.open
      }
    }
  },
  watch: {
    searchKey (newValue) {
      this.filterList(newValue)
    },
    disabled () {
      if (this.multipicklist) {
        this.localValue = []
      } else {
        this.localValue = ''
      }
    },
    options (options, oldOptions) {
      const oldOpt = _.cloneDeep(oldOptions)
      const newOpt = _.cloneDeep(options)

      const equal = arrayEquals(oldOpt, newOpt)

      if (!equal) {
        this.localValue = this.multipicklist ? [] : ''
        this.localOptions = options
        this.mapLocalOptions(this.options)
        this.filterList()
      }
    },
    value (value, oldValue) {
      this.localValue = value
    }
  },
  created () {
    if (this.value) {
      this.localValue = this.value
    }

    this.mapLocalOptions(this.options)

    this.filterList()

    this.$store.watch((state, getters) => getters['session/locale'],
      newValue => {
        this.mapLocalOptions(this.options)
        this.filterList()
      })
  },
  beforeDestroy () {
    this.showDeleteConfirmModal = false
    this.localDisabled = false
    this.localOptions = []
    this.localValue = this.multipicklist ? [] : ''
    this.open = false
    this.searchKey = ''
    this.selectedOption = {}
  }
}
</script>
<style lang="scss" scoped>
  .selected {
    fill: #4589EE;
  }

  .selection-box {
    height: fit-content;
  }

  svg {
    cursor: pointer;
  }

  .error {
    color: #C23933
  }

  .listbox {
    & > li:first-child {
      padding: 0.125rem 0.125rem 0.125rem 0rem
    }
    & > li {
      padding: 0.125rem 0.125rem 0.125rem 0rem
    }
  }
</style>
